import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useRealmApp } from '../RealmApp';

export default function useStoreMutations() {
  const app = useRealmApp();
  return {
    addStore: useAddStore(app.currentUser.id),
    updateStore: useUpdateStore(app.currentUser.id),
    deleteStore: useDeleteStore(app.currentUser.id),
  };
}

const AddStoreMutation = gql`
  mutation AddStore($store: StoreInsertInput!) {
    addedStore: insertOneStore(data: $store) {
      _id
      address1
      address2
      city
      country
      email
      coordinates
      image
      color
      language
      name
      phone
      phone2
      terminals
      webPage
      zip
      updateDatetime
      creationDatetime
    }
  }
`;

const UpdateStoreMutation = gql`
  mutation UpdateStore(
    $storeId: ObjectId!
    $ownerId: String!
    $updates: StoreUpdateInput!
  ) {
    updatedStore: updateOneStore(
      query: { _id: $storeId, owner_id: $ownerId }
      set: $updates
    ) {
      _id
      address1
      address2
      city
      country
      email
      coordinates
      image
      color
      language
      name
      phone
      phone2
      terminals
      webPage
      zip
      updateDatetime
      creationDatetime
    }
  }
`;

const DeleteStoreMutation = gql`
  mutation DeleteStore($storeId: ObjectId!, $ownerId: String!) {
    deletedStore: deleteOneStore(query: { _id: $storeId, owner_id: $ownerId }) {
      _id
    }
  }
`;

const StoreFieldsFragment = gql`
  fragment StoreFields on Store {
    _id
    address1
    address2
    city
    country
    email
    coordinates
    image
    color
    language
    name
    phone
    phone2
    terminals
    webPage
    zip
    updateDatetime
    creationDatetime
  }
`;

function useAddStore(ownerId) {
  const [addStoreMutation] = useMutation(AddStoreMutation, {
    // Manually save added Stores into the Apollo cache so that Store queries automatically update
    // For details, refer to https://www.apollographql.com/docs/react/data/mutations/#making-all-other-cache-updates
    update: (cache, { data: { addedStore } }) => {
      cache.modify({
        fields: {
          stores: (existingStores = []) => [
            ...existingStores,
            cache.writeFragment({
              data: addedStore,
              fragment: StoreFieldsFragment,
            }),
          ],
        },
      });
    },
  });
  return async (store) => {
    const {
      data: { addedStore },
    } = await addStoreMutation({
      variables: {
        store: {
          owner_id: ownerId,
          ...store,
        },
      },
    });
    return addedStore;
  };
}

function useUpdateStore(ownerId) {
  const [updateStoreMutation] = useMutation(UpdateStoreMutation);
  //
  return async (store, updates) => {
    const {
      data: { updatedStore },
    } = await updateStoreMutation({
      variables: { storeId: store._id, ownerId, updates },
    });
    return updatedStore;
  };
}

function useDeleteStore(ownerId) {
  const [deleteStoreMutation] = useMutation(DeleteStoreMutation, {
    update: (cache, { data: { deletedStore } }) => {
      cache.modify({
        fields: {
          stores: (existingStores = [], { readField }) =>
            existingStores.filter(
              (ref) => deletedStore._id !== readField('_id', ref)
            ),
        },
      });
    },
  });
  return async (store) => {
    const {
      data: { deletedStore },
    } = await deleteStoreMutation({
      variables: { storeId: store._id, ownerId },
    });
    return deletedStore;
  };
}
